import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Store, LogOut, TrendingUp, DollarSign, Users, Plus, X, Crown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BarOwnerDashboard = () => {
  const navigate = useNavigate();
  const [owner, setOwner] = useState(null);
  const [bars, setBars] = useState([]);
  const [myBar, setMyBar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  
  // Forms state
  const [newSpecial, setNewSpecial] = useState('');
  const [specials, setSpecials] = useState([]);
  const [adTitle, setAdTitle] = useState('');
  const [adDescription, setAdDescription] = useState('');
  const [adImage, setAdImage] = useState(null);
  const [adVideo, setAdVideo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('bar_owner_token');
    if (!token) {
      navigate('/bar-owner/auth');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchOwnerData();
  }, []);

  const fetchOwnerData = async () => {
    try {
      const [ownerRes, barsRes] = await Promise.all([
        axios.get(`${API}/bar-owner/me`),
        axios.get(`${API}/bars`)
      ]);
      
      setOwner(ownerRes.data);
      setBars(barsRes.data);
      
      if (ownerRes.data.bar_id) {
        const bar = barsRes.data.find(b => b.id === ownerRes.data.bar_id);
        setMyBar(bar);
        setSpecials(bar?.drink_specials || []);
        
        // Fetch advertisements
        const adsRes = await axios.get(`${API}/bars/${ownerRes.data.bar_id}/advertisements`);
        setAdvertisements(adsRes.data);
        
        // Fetch analytics if premium
        if (ownerRes.data.subscription_tier === 'premium') {
          const analyticsRes = await axios.get(`${API}/bar-owner/analytics`);
          setAnalytics(analyticsRes.data);
        }
      }
    } catch (e) {
      console.error(e);
      if (e.response?.status === 401) {
        localStorage.removeItem('bar_owner_token');
        navigate('/bar-owner/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaimBar = async (barId) => {
    try {
      await axios.post(`${API}/bar-owner/claim-bar`, null, { params: { bar_id: barId } });
      toast.success('Bar claimed successfully!');
      fetchOwnerData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to claim bar');
    }
  };

  const handleSubscribe = async (tier) => {
    try {
      await axios.post(`${API}/bar-owner/subscribe`, null, { params: { tier } });
      toast.success(`Subscribed to ${tier} plan!`);
      fetchOwnerData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to subscribe');
    }
  };

  const handleUpdateSpecials = async () => {
    try {
      await axios.put(`${API}/bar-owner/update-specials`, specials);
      toast.success('Drink specials updated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update specials');
    }
  };

  const handleAddSpecial = () => {
    if (newSpecial.trim()) {
      setSpecials([...specials, newSpecial.trim()]);
      setNewSpecial('');
    }
  };

  const handleRemoveSpecial = (index) => {
    setSpecials(specials.filter((_, i) => i !== index));
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', adTitle);
      formData.append('description', adDescription);
      if (adImage) formData.append('image', adImage);
      if (adVideo) formData.append('video', adVideo);

      await axios.post(`${API}/bar-owner/create-ad`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Advertisement created!');
      setAdTitle('');
      setAdDescription('');
      setAdImage(null);
      setAdVideo(null);
      fetchOwnerData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ad');
    }
  };

  const logout = () => {
    localStorage.removeItem('bar_owner_token');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/bar-owner/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Store className="w-6 h-6 text-indigo-400" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Bar Owner Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-indigo-600 text-white">
              {owner?.subscription_tier === 'none' ? 'Free' : owner?.subscription_tier}
            </Badge>
            <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Welcome */}
        <Card className="glass p-6 border border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-2">
            Welcome, {owner?.business_name}!
          </h2>
          <p className="text-slate-300">
            {myBar ? `Managing: ${myBar.name}` : 'No bar claimed yet'}
          </p>
        </Card>

        {/* Claim Bar */}
        {!myBar && (
          <Card className="glass p-6 border border-slate-700">
            <h3 className="text-xl font-semibold text-white mb-4">Claim Your Bar</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bars.filter(b => !b.claimed).map(bar => (
                <Card key={bar.id} className="glass p-4 border border-slate-600">
                  <h4 className="font-semibold text-white mb-2">{bar.name}</h4>
                  <p className="text-sm text-slate-400 mb-3">{bar.address}</p>
                  <Button
                    size="sm"
                    onClick={() => handleClaimBar(bar.id)}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    Claim This Bar
                  </Button>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Subscription Plans */}
        {myBar && owner?.subscription_status !== 'active' && (
          <Card className="glass p-6 border border-slate-700">
            <h3 className="text-xl font-semibold text-white mb-4">Choose Your Plan</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Basic Plan */}
              <Card className="glass p-6 border-2 border-slate-600">
                <div className="mb-4">
                  <h4 className="text-2xl font-bold text-white">Basic</h4>
                  <p className="text-3xl font-bold text-indigo-400 mt-2">$29<span className="text-lg text-slate-400">/mo</span></p>
                </div>
                <ul className="space-y-2 mb-6 text-slate-300">
                  <li>✓ Claim your bar</li>
                  <li>✓ Update drink specials</li>
                  <li>✓ Basic visibility</li>
                </ul>
                <Button
                  onClick={() => handleSubscribe('basic')}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  Subscribe to Basic
                </Button>
              </Card>

              {/* Premium Plan */}
              <Card className="glass p-6 border-2 border-yellow-500 relative">
                <Badge className="absolute -top-3 right-4 bg-yellow-500 text-black">
                  <Crown className="w-3 h-3 mr-1" />
                  Popular
                </Badge>
                <div className="mb-4">
                  <h4 className="text-2xl font-bold text-white">Premium</h4>
                  <p className="text-3xl font-bold text-yellow-400 mt-2">$79<span className="text-lg text-slate-400">/mo</span></p>
                </div>
                <ul className="space-y-2 mb-6 text-slate-300">
                  <li>✓ Everything in Basic</li>
                  <li>✓ Post promotional ads</li>
                  <li>✓ View analytics</li>
                  <li>✓ Featured placement</li>
                </ul>
                <Button
                  onClick={() => handleSubscribe('premium')}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                >
                  Subscribe to Premium
                </Button>
              </Card>
            </div>
          </Card>
        )}

        {/* Analytics (Premium Only) */}
        {owner?.subscription_tier === 'premium' && analytics && (
          <Card className="glass p-6 border border-slate-700">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-400" />
              Analytics
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card className="glass p-4 border border-slate-600">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-indigo-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.total_checkins}</p>
                    <p className="text-sm text-slate-400">Total Check-ins</p>
                  </div>
                </div>
              </Card>
              <Card className="glass p-4 border border-slate-600">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.checkins_last_30_days}</p>
                    <p className="text-sm text-slate-400">Last 30 Days</p>
                  </div>
                </div>
              </Card>
              <Card className="glass p-4 border border-slate-600">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold text-white">{analytics.unique_users}</p>
                    <p className="text-sm text-slate-400">Unique Visitors</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Age Demographics */}
            {analytics.age_demographics && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-white mb-3">Age Demographics</h4>
                <div className="grid grid-cols-5 gap-3">
                  {Object.entries(analytics.age_demographics).map(([range, count]) => (
                    <Card key={range} className="glass p-3 border border-slate-600 text-center">
                      <p className="text-sm text-slate-400">{range}</p>
                      <p className="text-2xl font-bold text-indigo-400">{count}</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Gender Breakdown */}
            {analytics.gender_breakdown && (
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">Gender Breakdown</h4>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(analytics.gender_breakdown).map(([gender, count]) => (
                    <Card key={gender} className="glass p-3 border border-slate-600 text-center">
                      <p className="text-sm text-slate-400 capitalize">{gender}</p>
                      <p className="text-2xl font-bold text-purple-400">{count}</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Update Drink Specials */}
        {myBar && owner?.subscription_status === 'active' && (
          <Card className="glass p-6 border border-slate-700">
            <h3 className="text-xl font-semibold text-white mb-4">Update Drink Specials</h3>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a new special..."
                  value={newSpecial}
                  onChange={(e) => setNewSpecial(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSpecial()}
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <Button onClick={handleAddSpecial} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                {specials.map((special, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                    <span className="text-slate-300">{special}</span>
                    <button
                      onClick={() => handleRemoveSpecial(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <Button onClick={handleUpdateSpecials} className="w-full bg-indigo-600 hover:bg-indigo-700">
                Save Changes
              </Button>
            </div>
          </Card>
        )}

        {/* Create Advertisement (Premium Only) */}
        {owner?.subscription_tier === 'premium' && myBar && (
          <Card className="glass p-6 border border-slate-700">
            <h3 className="text-xl font-semibold text-white mb-4">Create Advertisement</h3>
            <form onSubmit={handleCreateAd} className="space-y-4">
              <div>
                <Label className="text-white">Title</Label>
                <Input
                  value={adTitle}
                  onChange={(e) => setAdTitle(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Textarea
                  value={adDescription}
                  onChange={(e) => setAdDescription(e.target.value)}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Image (optional)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setAdImage(e.target.files[0])}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Video (optional)</Label>
                <Input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setAdVideo(e.target.files[0])}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                Create Advertisement
              </Button>
            </form>

            {/* Existing Ads */}
            {advertisements.length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-white mb-3">Your Advertisements</h4>
                <div className="space-y-3">
                  {advertisements.map(ad => (
                    <Card key={ad.id} className="glass p-4 border border-slate-600">
                      <h5 className="font-semibold text-white">{ad.title}</h5>
                      <p className="text-sm text-slate-400">{ad.description}</p>
                      {ad.image_url && (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${ad.image_url}`}
                          alt={ad.title}
                          className="mt-2 rounded-lg max-h-40 object-cover"
                        />
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default BarOwnerDashboard;
